import { Box, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FONT_MONO } from "../../theme/v2Tokens";

interface DetailHeaderProps {
  agentName: string;
  dateTime: string;
  duration: string;
  phoneNumber: string;
  onClose: () => void;
  onOpenInNewWindow?: () => void;
}

export default function DetailHeader({
  agentName,
  dateTime,
  duration,
  phoneNumber,
  onClose,
  onOpenInNewWindow,
}: DetailHeaderProps) {
  const { t } = useTranslation("common");
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        px: 2,
        py: 1.5,
        borderBottom: "1px solid var(--border-subtle)",
        flexShrink: 0,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="h3"
          sx={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "var(--text-primary)",
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          {agentName}
        </Typography>
        <Box
          component="span"
          sx={{
            fontFamily: FONT_MONO,
            fontSize: "0.75rem",
            color: "var(--text-secondary)",
            display: "block",
          }}
        >
          {dateTime} &middot; {duration} &middot; {phoneNumber}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0, ml: 1 }}>
        {onOpenInNewWindow && (
          <IconButton
            size="small"
            onClick={onOpenInNewWindow}
            aria-label={t("openInNewWindow")}
            sx={{
              width: 32,
              height: 32,
              color: "var(--text-tertiary)",
              "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-hover)" },
            }}
          >
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ width: 15, height: 15 }}
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </Box>
          </IconButton>
        )}

        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t("close")}
          sx={{
            width: 32,
            height: 32,
            color: "var(--text-tertiary)",
            "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-hover)" },
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{ width: 16, height: 16 }}
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </Box>
        </IconButton>
      </Box>
    </Box>
  );
}
